import { createSelector } from 'reselect'
import * as R from 'ramda'
import * as RE from 'helpers/ramda-ext'
import { concatMediaPathIfNeeded } from 'helpers/PathHelper'
import {
  ORDERED_PAYMENT_METHODS,
  PAYMENT_METHOD_ALMA_LABEL,
  PAYMENT_METHOD_FLOA3X4X_LABEL,
  PAYMENT_METHOD_FLOA4X_LABEL,
  PAYMENT_METHOD_ONEY_LABEL,
  PAYMENT_METHOD_PLEDG_LABEL
} from 'constants/paymentMethods'
import {
  getLabelsFormattedForTracking,
  getMostImportantDelayedPaymentType
} from 'domain/products'
import { injectLinkTo } from 'domain/resources'
import { Resource } from 'types/typescript'
import { getResources } from 'store/reducers/resources'
import {
  getIsHideReferencePriceFeatureEnabled,
  getIsPackageUnitDisclaimerFeatureEnabled,
  getIsPaymentBannerFeatureEnabled,
  getIsReviewsFeatureEnabled,
  getMediaPath,
  getOperationCode,
  getShouldDoubleThePriceFeatureEnabled
} from './application'
import { getCurrentResource } from './currentResource'
import { getFiltersProducts } from './filters'
import {
  GetCarouselMedias,
  GetCurrentProduct,
  GetCurrentProductDepartureDatesRange,
  GetCurrentProductKey,
  GetCurrentProductLabelsFormattedForTracking,
  GetCurrentProductMerchant,
  GetCurrentProductReviews,
  GetCurrentProductShouldDoubleThePrice,
  GetDisplayProductReviews,
  GetHasMap,
  GetHasOnlyOnePrice,
  GetHasPackages,
  GetHasPackagesError,
  GetIsCurrentProductOutOfStock,
  GetIsPaymentBannerDisplayable,
  GetIsProductFetched,
  GetLegalNoticeMSRP,
  GetLegalNotices,
  GetOrderedPaymentMethods,
  GetPaymentMethods,
  GetPaymentTypeForPaymentBanner,
  GetProductBottomText,
  GetProductCallPrice,
  GetProductCardMedia,
  GetProductContent,
  GetProductCoordinates,
  GetProductCustomLabels,
  GetProductEcomm,
  GetProductImages,
  GetProductLocationId,
  GetProductMapImage,
  GetProductPrice,
  GetProductPriceMsrp,
  GetProductPriceUnit,
  GetProductPriceValue,
  GetProductQuestion,
  GetProductSourceKey,
  GetProductTitle,
  GetShowPackagesDisclaimerPricesUnitTooltip,
  GetTabs,
  GetToLocationContent
} from './product.types'
import { getScope } from './scope'

export const getCurrentProduct: GetCurrentProduct = createSelector(
  getCurrentResource,
  getScope,
  getOperationCode,
  getResources,
  getIsHideReferencePriceFeatureEnabled,
  (product, scope, opeCode, allResources, shouldHideReferencePrice) => {
    const result = RE.whenNotNil(
      R.ifElse(
        R.compose(R.equals('product'), R.prop('type')),
        product => injectLinkTo(scope as Resource, opeCode, allResources)(product),
        R.always(null)
      )
    )(product)

    if (!shouldHideReferencePrice || !result?.ecomm?.price) return result

    return { ...result, ecomm: { ...result.ecomm, price: { ...result.ecomm.price, msrp: null } } }
  }
)

export const getProductCustomLabels: GetProductCustomLabels = createSelector(
  getCurrentProduct,
  R.path(['content', 'customLabels'])
)

export const getCurrentProductKey: GetCurrentProductKey = createSelector(
  getCurrentProduct,
  RE.whenNotNil(R.prop('key'))
)

export const getProductContent: GetProductContent = R.pipe(getCurrentProduct, R.prop('content'))
export const getProductEcomm: GetProductEcomm = R.pipe(getCurrentProduct, R.prop('ecomm'))
export const getHasOnlyOnePrice: GetHasOnlyOnePrice = R.pipe(getProductEcomm, R.prop('hasOnlyOnePrice'))
export const getProductSourceKey: GetProductSourceKey = R.pipe(getProductEcomm, R.prop('externalId'))
export const getProductBottomText: GetProductBottomText = R.pipe(getProductContent, R.path(['bottom', 'text']))
export const getToLocationContent: GetToLocationContent = R.pipe(getProductContent, R.prop('tolocation'))
export const getProductImages: GetProductImages = R.pipe(getToLocationContent, R.path(['medias', 'images']))

export const getProductCallPrice: GetProductCallPrice = createSelector(
  getProductEcomm,
  R.pipe(
    R.path(['price', 'price']),
    R.defaultTo(0)
  )
)

/*
  The `undefined` check of the hasPrice is here for retrocompat issue
  it should be removed in the future
*/
export const getProductPrice: GetProductPrice = createSelector(
  getFiltersProducts,
  getCurrentProduct,
  (filtersProducts, product) => {
    const filteredProduct = filtersProducts[product.key]

    if (filteredProduct !== undefined) {
      return (
        (filteredProduct.hasPrice || filteredProduct.hasPrice === undefined)
          ? R.pick(['price', 'msrp', 'unit'], filteredProduct)
          : null
      )
    }

    return (
      (product.ecomm.hasPrice || product.ecomm.hasPrice === undefined)
        ? R.path(['ecomm', 'price'], product)
        : null
    )
  }
)

export const getProductPriceValue: GetProductPriceValue = createSelector(
  getProductPrice,
  productPrice => productPrice?.price ?? null
)

export const getProductPriceUnit: GetProductPriceUnit = createSelector(
  getProductPrice,
  productPrice => productPrice?.unit ?? null
)

export const getProductPriceMsrp: GetProductPriceMsrp = createSelector(
  getProductPrice,
  productPrice => productPrice?.msrp ?? null
)

export const getProductCardMedia: GetProductCardMedia = createSelector(
  getToLocationContent,
  R.identity(toLocation => (
    R.path(['medias', 'ev', 'large'], toLocation) ||
    R.path(['medias', 'ev', 'small'], toLocation)
  ))
)

export const getTabs: GetTabs = createSelector(
  getToLocationContent,
  R.path(['description', 'tabs'])
)

export const getIsProductFetched: GetIsProductFetched = createSelector(
  getCurrentProduct,
  R.prop('fetched')
)

export const getProductTitle: GetProductTitle = createSelector(
  getProductContent,
  R.prop('title')
)

export const getProductQuestion: GetProductQuestion = createSelector(
  getProductContent,
  R.path(['tolocation', 'question'])
)

export const getLegalNotices: GetLegalNotices = createSelector(
  getProductBottomText,
  RE.whenNotNil(
    R.pipe(
      R.map(R.prop('title')),
      R.filter(RE.isNotEmptyOrNil)
    )
  )
)

export const getCarouselMedias: GetCarouselMedias = createSelector(
  getMediaPath,
  getProductImages,
  (mediaPath, medias) => ((medias && R.is(Array, medias))
    ? medias.map(
      R.over(R.lensProp('media'), concatMediaPathIfNeeded(mediaPath))
    )
    : [])
)

export const getProductLocationId: GetProductLocationId = createSelector(
  getToLocationContent,
  toLocation => toLocation?.medias?.map?.locationId
)

export const getProductMapImage: GetProductMapImage = createSelector(
  getToLocationContent,
  toLocation => toLocation?.medias?.map?.image
)

export const getProductCoordinates: GetProductCoordinates = createSelector(
  getProductEcomm,
  ecomm => ecomm?.coordinates
)

export const getHasMap: GetHasMap = createSelector(
  getProductMapImage,
  getProductCoordinates,
  (mapImage, coordinates) =>
    mapImage !== undefined ||
    (coordinates?.latitude !== undefined &&
      coordinates?.latitude !== 0 &&
      coordinates?.longitude !== undefined &&
      coordinates?.longitude !== 0)
)

export const getIsCurrentProductOutOfStock: GetIsCurrentProductOutOfStock = createSelector(
  getCurrentProduct,
  R.prop('outOfStock')
)

export const getDisplayProductReviews: GetDisplayProductReviews = createSelector(
  getCurrentProduct,
  getIsReviewsFeatureEnabled,
  (currentProduct, enableReviews) => (
    enableReviews && R.path(['ecomm', 'displayRatings'], currentProduct)
  )
)

export const getCurrentProductReviews: GetCurrentProductReviews = createSelector(
  getCurrentProduct,
  R.prop('reviews')
)

export const getPaymentMethods: GetPaymentMethods = createSelector(
  getProductEcomm,
  R.prop('paymentType')
)

export const getCurrentProductShouldDoubleThePrice: GetCurrentProductShouldDoubleThePrice = createSelector(
  getShouldDoubleThePriceFeatureEnabled,
  getProductEcomm,
  (isShouldDoubleThePriceFeatureEnabled, ecomm) => (
    isShouldDoubleThePriceFeatureEnabled &&
    Boolean(ecomm?.shouldDoubleThePrice)
  )
)

export const getIsPaymentBannerDisplayable: GetIsPaymentBannerDisplayable = createSelector(
  getCurrentProduct,
  getIsPaymentBannerFeatureEnabled,
  (currentProduct, isPaymentBannerFeatureEnabled) => {
    if (currentProduct && isPaymentBannerFeatureEnabled) {
      const paymentMethods = R.path(['ecomm', 'paymentType'], currentProduct) || []

      return (Boolean(paymentMethods.find(({ key }) => (
        key === PAYMENT_METHOD_ALMA_LABEL ||
        key === PAYMENT_METHOD_FLOA3X4X_LABEL ||
        key === PAYMENT_METHOD_FLOA4X_LABEL ||
        key === PAYMENT_METHOD_ONEY_LABEL ||
        key === PAYMENT_METHOD_PLEDG_LABEL
      ))))
    }

    return false
  }
)

export const getPaymentTypeForPaymentBanner: GetPaymentTypeForPaymentBanner = createSelector(
  getCurrentProduct,
  getIsPaymentBannerFeatureEnabled,
  (currentProduct, isPaymentBannerFeatureEnabled) => {
    if (currentProduct && isPaymentBannerFeatureEnabled) {
      return (
        getMostImportantDelayedPaymentType(currentProduct.ecomm.paymentType)
      )
    }
  }
)

export const getOrderedPaymentMethods: GetOrderedPaymentMethods = createSelector(
  getPaymentMethods,
  paymentMethods => {
    const expectedPaymentMethods = R.map(R.prop('key'), paymentMethods)
    return ORDERED_PAYMENT_METHODS
      .filter(method => expectedPaymentMethods.includes(method))
  }
)

export const getHasPackages: GetHasPackages = createSelector(
  getProductEcomm,
  R.prop('hasPackages')
)

export const getHasPackagesError: GetHasPackagesError = createSelector(
  getIsProductFetched,
  getCurrentProduct,
  getHasPackages,
  (isProductFetched, product, hasPackages) => (
    hasPackages && isProductFetched && (!product.resources || !product.resources.length)
  )
)

export const getShowPackagesDisclaimerPricesUnitTooltip: GetShowPackagesDisclaimerPricesUnitTooltip = createSelector(
  getHasPackagesError,
  getIsPackageUnitDisclaimerFeatureEnabled,
  (hasPackagesError, withTooltipFeature) => (
    !hasPackagesError &&
    withTooltipFeature
  )
)

export const getCurrentProductDepartureDatesRange: GetCurrentProductDepartureDatesRange = createSelector(
  getProductEcomm,
  R.prop('departureDatesRange')
)

export const getCurrentProductLabelsFormattedForTracking: GetCurrentProductLabelsFormattedForTracking = createSelector(
  getProductEcomm,
  (ecomm) => getLabelsFormattedForTracking(ecomm.labels)
)

export const getCurrentProductMerchant: GetCurrentProductMerchant = createSelector(
  getProductEcomm,
  R.prop('merchantLabel')
)

export const getLegalNoticeMSRP: GetLegalNoticeMSRP = createSelector(
  getProductEcomm,
  R.prop('legalNoticeMSRP')
)
